import React, { useRef, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Box } from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  EditOutlined as EditIcon,
  SwapVertOutlined as UrgencyIcon,
  CheckCircleOutline as ResolveIcon,
  DeleteOutline as DeleteIcon,
  RestartAlt as ReopenIcon,
  LinkOutlined as LinkIcon,
  AddCircleOutline as CreateWoIcon,
  SendOutlined as CreateRfsIcon,
  LinkOff as UnlinkIcon,
  UndoOutlined as RecallIcon,
} from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { SrTab, EnrichedServiceReport } from "../../store/serviceReportsSlice";
import accountContext from "../../../../account/accountContext";
import { Roles } from "../../../../account/models/AccountModels";

// ─── Types ──────────────────────────────────────────────────

export type SrAction =
  | "edit"
  | "changeUrgency"
  | "resolve"
  | "delete"
  | "linkToWo"
  | "createWo"
  | "createRfs"
  | "escalateToRfs"
  | "unlinkFromWo"
  | "recall"
  | "reopen";

interface OverflowMenuProps {
  item: EnrichedServiceReport;
  onAction: (action: SrAction, item: EnrichedServiceReport) => void;
  translate: (key: string) => string;
}

// ─── Menu item config per tab ────────────────────────────────

interface MenuItemConfig {
  action: SrAction;
  labelKey: string;
  icon: React.ReactNode;
  danger?: boolean;
}

function getMenuItems(tab: SrTab): MenuItemConfig[] {
  switch (tab) {
    case "untriaged":
      return [
        { action: "edit", labelKey: "serviceReports.actions.edit", icon: <EditIcon sx={{ fontSize: 16 }} /> },
        { action: "changeUrgency", labelKey: "serviceReports.actions.changeUrgency", icon: <UrgencyIcon sx={{ fontSize: 16 }} /> },
        { action: "createWo", labelKey: "serviceReports.actions.createWo", icon: <CreateWoIcon sx={{ fontSize: 16 }} /> },
        { action: "linkToWo", labelKey: "serviceReports.actions.linkToWo", icon: <LinkIcon sx={{ fontSize: 16 }} /> },
        { action: "createRfs", labelKey: "serviceReports.actions.createRfs", icon: <CreateRfsIcon sx={{ fontSize: 16 }} /> },
        { action: "resolve", labelKey: "serviceReports.actions.resolve", icon: <ResolveIcon sx={{ fontSize: 16 }} /> },
        { action: "delete", labelKey: "serviceReports.actions.delete", icon: <DeleteIcon sx={{ fontSize: 16 }} />, danger: true },
      ];
    case "internal":
      return [
        { action: "edit", labelKey: "serviceReports.actions.edit", icon: <EditIcon sx={{ fontSize: 16 }} /> },
        { action: "changeUrgency", labelKey: "serviceReports.actions.changeUrgency", icon: <UrgencyIcon sx={{ fontSize: 16 }} /> },
        { action: "escalateToRfs", labelKey: "serviceReports.actions.escalateToRfs", icon: <CreateRfsIcon sx={{ fontSize: 16 }} /> },
        { action: "resolve", labelKey: "serviceReports.actions.resolve", icon: <ResolveIcon sx={{ fontSize: 16 }} /> },
        { action: "unlinkFromWo", labelKey: "serviceReports.actions.unlinkFromWo", icon: <UnlinkIcon sx={{ fontSize: 16 }} /> },
      ];
    case "external":
      return [
        { action: "edit", labelKey: "serviceReports.actions.edit", icon: <EditIcon sx={{ fontSize: 16 }} /> },
        { action: "resolve", labelKey: "serviceReports.actions.resolve", icon: <ResolveIcon sx={{ fontSize: 16 }} /> },
        { action: "recall", labelKey: "serviceReports.actions.recall", icon: <RecallIcon sx={{ fontSize: 16 }} /> },
      ];
    case "resolved":
      return [
        { action: "edit", labelKey: "serviceReports.actions.edit", icon: <EditIcon sx={{ fontSize: 16 }} /> },
        { action: "reopen", labelKey: "serviceReports.actions.reopen", icon: <ReopenIcon sx={{ fontSize: 16 }} /> },
        { action: "delete", labelKey: "serviceReports.actions.delete", icon: <DeleteIcon sx={{ fontSize: 16 }} />, danger: true },
      ];
    default:
      return [];
  }
}

// ─── Component ──────────────────────────────────────────────

const ServiceReportOverflowMenu: React.FC<OverflowMenuProps> = ({
  item,
  onAction,
  translate,
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLDivElement>(null);
  const canManage = accountContext.user?.role !== Roles.WORKER;

  // Filter out "delete" action for non-Admin users (hard delete = Admin only)
  const menuItems = getMenuItems(item.tab).filter(
    (mi) => mi.action !== "delete" || canManage
  );

  // Calculate menu position from button's bounding rect
  const updateMenuPosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      left: rect.right,
    });
  }, []);

  // Position menu when opened
  useEffect(() => {
    if (open) updateMenuPosition();
  }, [open, updateMenuPosition]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  // Close on scroll (any scrollable ancestor)
  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    window.addEventListener("scroll", handler, true);
    return () => window.removeEventListener("scroll", handler, true);
  }, [open]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleItemClick = (e: React.MouseEvent, menuItem: MenuItemConfig) => {
    e.stopPropagation();
    setOpen(false);
    onAction(menuItem.action, item);
  };

  if (menuItems.length === 0) return null;

  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      {/* Three-dot button */}
      <Box
        ref={btnRef}
        onClick={handleToggle}
        sx={{
          width: "28px",
          height: "28px",
          borderRadius: "6px",
          backgroundColor: open ? "#E8E5DF" : "#F2F0EB",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.15s ease",
          flexShrink: 0,
          "&:hover": {
            backgroundColor: "#E8E5DF",
            "& .overflow-icon": {
              color: "#D4572B",
            },
          },
        }}
      >
        <MoreVertIcon
          className="overflow-icon"
          sx={{
            fontSize: "18px",
            color: open ? "#D4572B" : "#8C9099",
            transition: "color 0.15s ease",
          }}
        />
      </Box>

      {/* Dropdown menu — rendered via portal to escape card overflow:hidden */}
      {open &&
        createPortal(
          <Box
            ref={menuRef}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{
              position: "fixed",
              top: `${menuPos.top}px`,
              left: `${menuPos.left}px`,
              transform: "translateX(-100%)",
              zIndex: 1300,
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)",
              minWidth: "180px",
              py: "6px",
              animation: "menuFadeIn 0.15s ease-out",
              "@keyframes menuFadeIn": {
                "0%": { opacity: 0, transform: "translateX(-100%) scale(0.95)" },
                "100%": { opacity: 1, transform: "translateX(-100%) scale(1)" },
              },
            }}
          >
            {menuItems.map((menuItem, idx) => {
              const isDanger = menuItem.danger;

              return (
                <React.Fragment key={menuItem.action}>
                  {/* Separator before danger items */}
                  {isDanger && idx > 0 && (
                    <Box sx={{ height: "1px", backgroundColor: "#F2F0EB", mx: "8px", my: "4px" }} />
                  )}
                  <Box
                    onClick={(e) => handleItemClick(e, menuItem)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      px: "14px",
                      py: "9px",
                      cursor: "pointer",
                      fontFamily: dt.font,
                      fontSize: "13px",
                      fontWeight: 500,
                      color: isDanger ? "#D4572B" : "#3D3D3D",
                      transition: "all 0.12s ease",
                      "&:hover": {
                        backgroundColor: isDanger
                          ? "rgba(212,87,43,0.08)"
                          : "rgba(212,87,43,0.06)",
                        "& .menu-icon": {
                          color: "#D4572B",
                        },
                      },
                    }}
                  >
                    <Box
                      className="menu-icon"
                      sx={{
                        display: "flex",
                        color: isDanger ? "#D4572B" : "#8C9099",
                        transition: "color 0.12s ease",
                      }}
                    >
                      {menuItem.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>{translate(menuItem.labelKey)}</Box>
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>,
          document.body,
        )}
    </Box>
  );
};

export default ServiceReportOverflowMenu;
